import React, { useState } from "react";
import {
  AppBar,
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Stack,
  Toolbar,
  Typography,
  useScrollTrigger,
} from "@mui/material";
import { Menu as MenuIcon, Close, LoginOutlined } from "@mui/icons-material";
import { Link as RouterLink, useLocation } from "react-router-dom";
import Logo from "../../logo1_ai copy436_alpha.png";
import { tokens, radius } from "../../theme/theme";
import { useSiteConfig } from "../SiteConfigContext";
import { CHRISTMAS, CHROME, HALLOWEEN } from "../halloween/season";
import HeaderGarland from "../halloween/HeaderGarland";
import { WitchHat } from "../halloween/sprites";
import Sprite from "../christmas/Sprite";
import { ICONS } from "../christmas/atlas";
import { IcicleEdge } from "../christmas/Edges";

const NAV = [
  { label: "Головна", to: "/" },
  { label: "Каталог", to: "/katalog" },
  { label: "Кросворд дня", to: "/krosvord" },
  { label: "Контакти", to: "/kontakty" },
];

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const scrolled = useScrollTrigger({ disableHysteresis: true, threshold: 8 });
  const { maintenance } = useSiteConfig();

  /**
   * In maintenance mode the header is still shown on the pages that keep
   * working (a shared listing link, the crossword, contacts), so it must not
   * offer a route to a page that is switched off. "Головна" stays — it leads to
   * the maintenance page, which is the honest answer.
   */
  const nav = maintenance
    ? NAV.filter(function (link) {
        return link.to !== "/katalog";
      })
    : NAV;

  const isActive = (to) =>
    to === "/" ? location.pathname === "/" : location.pathname.indexOf(to) === 0;

  return (
    <React.Fragment>
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={
          CHROME
            ? {
                // The night starts at the top of the page: same colour as the
                // top of the hero's sky, so header and hero read as one scene.
                backgroundColor: scrolled ? CHROME.barScrolled : CHROME.bar,
                backdropFilter: scrolled ? "saturate(160%) blur(14px)" : "none",
                borderBottom: "1px solid " + (scrolled ? CHROME.border : "transparent"),
                transition: "background-color .2s ease, border-color .2s ease",
              }
            : {
                backgroundColor: scrolled ? "rgba(255,255,255,0.88)" : tokens.surface,
                backdropFilter: scrolled ? "saturate(180%) blur(14px)" : "none",
                borderBottom: "1px solid " + (scrolled ? tokens.line : "transparent"),
                transition: "background-color .2s ease, border-color .2s ease",
              }
        }
      >
        <Container maxWidth="lg">
          <Toolbar disableGutters sx={{ minHeight: { xs: 62, md: 76 }, gap: 2 }}>
            <Box
              component={RouterLink}
              to="/"
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.2,
                textDecoration: "none",
                color: "inherit",
                mr: "auto",
              }}
            >
              <Box sx={{ position: "relative", display: "flex" }}>
                <Box
                  component="img"
                  src={Logo}
                  alt="LeoCity"
                  sx={{
                    height: { xs: 34, md: 42 },
                    // White on the night header, as the footer already does.
                    filter: CHROME ? "brightness(0) invert(1)" : "none",
                  }}
                />
                {HALLOWEEN && (
                  <WitchHat
                    style={{
                      position: "absolute",
                      // Perched on the tallest of the logo's towers.
                      top: -17,
                      left: "calc(60% - 17px)",
                      width: 34,
                      transform: "rotate(12deg)",
                      pointerEvents: "none",
                    }}
                  />
                )}
                {CHRISTMAS && (
                  <Sprite
                    atlas={ICONS}
                    name="santaHat"
                    scale={0.33}
                    style={{
                      position: "absolute",
                      // Perched on the tallest of the logo's towers.
                      top: -17,
                      left: "calc(60% - 18px)",
                      transform: "rotate(14deg)",
                      pointerEvents: "none",
                    }}
                  />
                )}
              </Box>
              <Box sx={{ display: { xs: "none", sm: "block" }, lineHeight: 1 }}>
                <Typography
                  variant="h5"
                  component="span"
                  sx={{
                    display: "block",
                    letterSpacing: "-0.02em",
                    color: CHROME ? "#fff" : undefined,
                  }}
                >
                  LeoCity
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    display: "block",
                    mt: 0.2,
                    color: CHROME ? "rgba(255,255,255,0.55)" : tokens.ink400,
                  }}
                >
                  Оренда житла у Львові
                </Typography>
              </Box>
            </Box>

            <Stack
              direction="row"
              spacing={0.5}
              sx={{ display: { xs: "none", md: "flex" } }}
            >
              {nav.map((link) => (
                <Button
                  key={link.to}
                  component={RouterLink}
                  to={link.to}
                  size="small"
                  sx={
                    CHROME
                      ? {
                          px: 1.8,
                          color: isActive(link.to) ? CHROME.accentText : "rgba(255,255,255,0.8)",
                          backgroundColor: isActive(link.to) ? CHROME.accentBg : "transparent",
                          "&:hover": { backgroundColor: "rgba(255,255,255,0.08)" },
                        }
                      : {
                          px: 1.8,
                          color: isActive(link.to) ? tokens.blue700 : tokens.ink700,
                          backgroundColor: isActive(link.to) ? tokens.blue50 : "transparent",
                          "&:hover": { backgroundColor: tokens.blue50 },
                        }
                  }
                >
                  {link.label}
                </Button>
              ))}
            </Stack>

            <Button
              component={RouterLink}
              to="/admin"
              variant="outlined"
              size="small"
              startIcon={<LoginOutlined sx={{ fontSize: 18 }} />}
              sx={{
                display: { xs: "none", md: "inline-flex" },
                ml: 1,
                ...(CHROME
                  ? {
                      color: "#fff",
                      borderColor: "rgba(255,255,255,0.28)",
                      "&:hover": {
                        borderColor: CHROME.accent,
                        backgroundColor: CHROME.accentHover,
                      },
                    }
                  : {}),
              }}
            >
              Вхід для рієлторів
            </Button>

            <IconButton
              aria-label="Меню"
              onClick={() => setOpen(true)}
              sx={{
                display: { xs: "inline-flex", md: "none" },
                color: CHROME ? "#fff" : undefined,
              }}
            >
              <MenuIcon />
            </IconButton>
          </Toolbar>
        </Container>
        {HALLOWEEN && <HeaderGarland />}
        {CHRISTMAS && <IcicleEdge />}
      </AppBar>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: 280, borderRadius: 0 } }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end" }}>
          <IconButton aria-label="Закрити" onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <List sx={{ px: 1 }}>
          {nav.map((link) => (
            <ListItemButton
              key={link.to}
              component={RouterLink}
              to={link.to}
              onClick={() => setOpen(false)}
              selected={isActive(link.to)}
              sx={{ borderRadius: radius.md, mb: 0.5 }}
            >
              <ListItemText
                primary={link.label}
                primaryTypographyProps={{ fontWeight: 700 }}
              />
            </ListItemButton>
          ))}
        </List>
        <Box sx={{ px: 2, mt: 1 }}>
          <Button
            component={RouterLink}
            to="/admin"
            onClick={() => setOpen(false)}
            fullWidth
            variant="outlined"
            startIcon={<LoginOutlined />}
          >
            Вхід для рієлторів
          </Button>
        </Box>
      </Drawer>
    </React.Fragment>
  );
}
